import { useRef, useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import { confirmDialog } from 'primereact/confirmdialog'
import { Controller, useFormContext } from 'react-hook-form'
import { z } from 'zod'

import PageHeader from '../../../components/admin/PageHeader.js'
import ResourceTable, { type ColumnDef } from '../../../components/admin/ResourceTable.js'
import ResourceDrawer from '../../../components/admin/ResourceDrawer.js'
import DurationInput from '../../../components/admin/DurationInput.js'
import { ZodForm, FormText, FormDropdown } from '../../../components/forms/index.js'
import { useAdminResource } from '../../../hooks/useAdminApi.js'
import { useContactCenterQueues } from '../../../hooks/useContactCenterQueues.js'
import { formatDuration } from '../../../utils/formatDate.js'

// ---------------------------------------------------------------------------
// Types — derived from apps/api/src/routes/conversations/admin/ola-policies.ts
// + packages/conversations/src/schema/tenant-tables.ts (ola_policies table)
// ---------------------------------------------------------------------------

export interface OlaPolicy {
  id: string
  name: string
  responseMinutes: number
  businessHoursConfigId: string | null
  appliesToConditions: Record<string, unknown>
  sortOrder: number
  appliesToQueueId: string | null
  escalationQueueId: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
}

const OlaSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  responseMinutes: z.number().int().positive('Response must be positive'),
  appliesToQueueId: z.string().optional().or(z.literal('')),
  isActive: z.boolean(),
})

type OlaFormValues = z.infer<typeof OlaSchema>

function FormDuration({ name, label }: { name: string; label: string }): JSX.Element {
  const { control } = useFormContext()
  return (
    <div className="mb-3 flex flex-col gap-1">
      <span className="text-sm font-medium">{label}</span>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <DurationInput
            value={typeof field.value === 'number' ? field.value : 0}
            onChange={field.onChange}
            min={1}
          />
        )}
      />
    </div>
  )
}

export default function OlaPoliciesPage(): JSX.Element {
  const { items, loading, create, update, remove } = useAdminResource<OlaPolicy>(
    '/admin/conversations/ola-policies',
    { responseKey: 'rows' },
  )
  const { items: queues } = useContactCenterQueues()

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<OlaPolicy | null>(null)
  const [copySource, setCopySource] = useState<OlaPolicy | null>(null)
  const formRef = useRef<HTMLFormElement>(null)

  function openCreate(): void {
    setEditing(null)
    setCopySource(null)
    setDrawerOpen(true)
  }

  function openEdit(row: OlaPolicy): void {
    setEditing(row)
    setCopySource(null)
    setDrawerOpen(true)
  }

  function openCopy(row: OlaPolicy): void {
    setEditing(null)
    setCopySource(row)
    setDrawerOpen(true)
  }

  function closeDrawer(): void {
    setDrawerOpen(false)
    setEditing(null)
    setCopySource(null)
  }

  async function handleSubmit(data: OlaFormValues): Promise<void> {
    const payload = {
      name: data.name.trim(),
      responseMinutes: data.responseMinutes,
      appliesToQueueId: data.appliesToQueueId ? data.appliesToQueueId : undefined,
      isActive: data.isActive,
    }
    if (editing) await update(editing.id, payload)
    else await create(payload)
    closeDrawer()
  }

  function handleDelete(row: OlaPolicy): void {
    confirmDialog({
      message: `Permanently delete "${row.name}"? This cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => void remove(row.id),
    })
  }

  const columns: ColumnDef<OlaPolicy>[] = [
    { label: 'Name', accessor: 'name' },
    { label: 'Response', accessor: (r) => formatDuration(r.responseMinutes * 60_000) },
    {
      label: 'Queue',
      accessor: (r) => queues.find((q) => q.id === r.appliesToQueueId)?.name ?? '—',
    },
    { label: 'Active', accessor: (r) => (r.isActive ? 'Yes' : 'No') },
  ]

  const defaultValues: OlaFormValues = editing
    ? {
        name: editing.name,
        responseMinutes: editing.responseMinutes,
        appliesToQueueId: editing.appliesToQueueId ?? '',
        isActive: editing.isActive,
      }
    : copySource
      ? {
          name: `Copy of ${copySource.name}`,
          responseMinutes: copySource.responseMinutes,
          appliesToQueueId: copySource.appliesToQueueId ?? '',
          isActive: copySource.isActive,
        }
      : { name: '', responseMinutes: 15, appliesToQueueId: '', isActive: true }

  const queueOptions = [
    { label: '— None —', value: '' },
    ...queues.map((q) => ({ label: q.name, value: q.id })),
  ]

  return (
    <div className="p-4">
      <PageHeader
        title="OLA Policies"
        description="Configure internal operational response timers per queue."
        actions={<Button label="+ New OLA Policy" onClick={openCreate} />}
      />
      <ResourceTable<OlaPolicy>
        columns={columns}
        rows={items}
        loading={loading}
        actions={['edit', 'copy', 'delete']}
        onEdit={openEdit}
        onCopy={openCopy}
        onDelete={handleDelete}
        empty={{
          title: 'No OLA policies',
          description: 'Create a policy to track internal response timers.',
          action: <Button label="+ New OLA Policy" onClick={openCreate} />,
        }}
      />
      <ResourceDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        onSave={async () => {}}
        formRef={formRef}
        title={editing ? `Edit OLA Policy: ${editing.name}` : copySource ? `Copy OLA Policy: ${copySource.name}` : 'New OLA Policy'}
      >
        <ZodForm schema={OlaSchema} defaultValues={defaultValues} onSubmit={handleSubmit}>
          <FormText name="name" label="Name" required autoFocus />
          <FormDuration name="responseMinutes" label="Response" />
          <FormDropdown name="appliesToQueueId" label="Applies to Queue" options={queueOptions} />
          <button type="submit" className="rounded bg-[#16273F] px-4 py-2 text-white">
            Save
          </button>
        </ZodForm>
      </ResourceDrawer>
    </div>
  )
}
